import { skipToken } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

import { useAsyncDataFromQuery } from "@tsmono/react/hooks";
import { AsyncData, data as asyncData, loading } from "@tsmono/util";

import { Log, LogFetchState, LogHeader } from "../client/api/types";

import { currentDatabase } from "./databaseInstance";
import { logKey, useLogs } from "./logsContent";
import { fetchLog } from "./replicationControl";

/**
 * One log's entity row as a per-entity, db-backed cache entry. The `queryFn`
 * is the re-seed path only (an evicted/unmounted entry re-reads its
 * IndexedDB row on remount); the sink's guarded pushes own freshness while
 * the entry is observed. `key` must be the resolved row name; idles
 * (`skipToken`, reads as loading) without one.
 */
const useLogRow = (
  logDir: string,
  key: string | undefined
): AsyncData<Log | null> =>
  useAsyncDataFromQuery({
    queryKey: logKey(logDir, key ?? ""),
    // Returns null on a miss — react-query forbids undefined from a queryFn.
    queryFn:
      key === undefined
        ? skipToken
        : async () => {
            const db = currentDatabase();
            return (db ? await db.readLogRow(key) : null) ?? null;
          },
    staleTime: Infinity,
    // default gcTime: eviction is desired — IndexedDB re-seeds on remount.
  });

/**
 * One log at detailed depth: its header. `data(undefined)` when no file is
 * given (idle); `error` is a RETRIEVAL failure — the db re-seed read failing
 * or the row's retrieval facts recording a fetch error (eval errors are
 * inside `data`). Mounting is demand: each (dir, file) requests detailed
 * depth from the engine, whose read-through resolves instantly on a cached
 * row and refreshes in the background.
 *
 * `opts.demand` distinguishes WHO is asking (required — every mount states
 * its role): `"active"` declares "someone is looking at this log" — only the
 * selection binding (`useSelectedLogDetail`) and `LogLoadController` use it.
 * Every other mount (sample-adjacent hooks: summaries, pending-samples,
 * running-sample) just needs the data to exist, so it is passive —
 * ensure-presence only, never bumping `details_settled_seq` or forcing a
 * server-cache-bypassing refresh. Without this split, switching tabs
 * (mounting a passive consumer for the ALREADY-selected log) would refire
 * `LogLoadController` as if a new log had loaded.
 */
export const useLogHeader = (
  logDir: string,
  logFile: string | undefined,
  opts: { demand: "active" | "passive" }
): AsyncData<LogHeader | undefined> => {
  const demand = opts.demand;
  const logs = useLogs(logDir);
  // The queryKey must use the resolved row name so it matches sink pushes;
  // while the listing collection settles, the file itself is the fallback.
  const key =
    logFile === undefined
      ? undefined
      : (logs.data?.find((row) => row.name.endsWith(logFile))?.name ?? logFile);
  const row = useLogRow(logDir, key);
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (logFile !== undefined) {
      // Failures land in the row's retrieval facts (surfaced below).
      void fetchLog(logDir, logFile, { passive: demand !== "active" }).catch(
        () => {}
      );
    }
  }, [logDir, logFile, demand]);
  return useMemo<AsyncData<LogHeader | undefined>>(() => {
    if (logFile === undefined) {
      return asyncData(undefined);
    }
    if (logs.error) {
      return { loading: false, error: logs.error };
    }
    if (row.error) {
      return row;
    }
    const header = row.data?.header;
    if (header !== undefined) {
      return asyncData<LogHeader | undefined>(header);
    }
    const message = row.data?.details_fetch_error;
    return message !== undefined
      ? { loading: false, error: new Error(message) }
      : loading;
  }, [logs.error, row, logFile]);
};

/**
 * A single log's retrieval facts (fetch errors/attempts/settled-seq — a
 * domain separate from eval status/error), for detail-path consumers (e.g.
 * a badge on the currently-open log, the load controller's settle guard).
 * Reads the same per-entity row `useLogHeader` does; mounting it is what makes the
 * row's engine pushes observed instead of guarded no-ops. `name` must be the
 * resolved row name; idles (reads as loading) without one. `data` is
 * undefined when the row isn't resident yet.
 */
export const useLogFetchState = (
  logDir: string,
  name: string | undefined
): AsyncData<LogFetchState | undefined> => {
  const row = useLogRow(logDir, name);
  return useMemo<AsyncData<LogFetchState | undefined>>(
    () => (row.loading || row.error ? row : asyncData(row.data ?? undefined)),
    [row]
  );
};
